import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAcceptanceList, useDebounce, useRequestPaymentDelay } from '@saiens/shared/hooks';
import { formatDate } from '@saiens/shared/utils';
import type { Acceptance, PaymentDelayState } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ChevronRight, ArrowLeft, Clock, X } from 'lucide-react';

type PaymentFilter = 'unpaid' | 'paid' | 'all';

const DELAY_STATE_LABELS: Record<PaymentDelayState, string> = {
  none: '',
  pending: '延後待審核',
  approved: '延後已核准',
  rejected: '延後已拒絕',
};

const DELAY_STATE_STYLES: Record<PaymentDelayState, string> = {
  none: '',
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-gray-100 text-gray-500',
};

export function PaymentStatusPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [filter, setFilter] = useState<PaymentFilter>('unpaid');
  const [myOrders, setMyOrders] = useState(false);
  const [delayModalTarget, setDelayModalTarget] = useState<Acceptance | null>(null);

  const { data, isLoading, error } = useAcceptanceList({
    search: debouncedSearch,
    myOrders: myOrders || undefined,
    unpaidOnly: filter === 'unpaid' ? true : undefined,
    paidOnly: filter === 'paid' ? true : undefined,
    invoicedDone: filter === 'all' ? true : undefined,
    limit: 50,
  });

  const items = data?.items ?? [];

  return (
    <div className="px-4 py-6">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/acceptance" className="text-gray-500">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">案件付款狀態</h1>
          <p className="text-sm text-gray-500">追蹤已開帳案件的付款進度</p>
        </div>
      </div>

      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋訂單編號、案名、客戶..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <FilterChip
            label="未付款"
            active={filter === 'unpaid'}
            onClick={() => setFilter('unpaid')}
          />
          <FilterChip
            label="已付款"
            active={filter === 'paid'}
            onClick={() => setFilter('paid')}
          />
          <FilterChip
            label="全部"
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          <div className="mx-1 border-l border-gray-300" />
          <FilterChip
            label="我的案件"
            active={myOrders}
            onClick={() => setMyOrders((prev) => !prev)}
          />
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : !items || items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            {filter === 'unpaid' ? '沒有未付款的案件' : '沒有資料'}
          </div>
        ) : (
          <div className="divide-y">
            {items.map((acc) => (
              <PaymentCard
                key={acc.id}
                acceptance={acc}
                onRequestDelay={() => setDelayModalTarget(acc)}
              />
            ))}
          </div>
        )}
      </div>

      {data && items.length > 0 && (
        <p className="mt-4 text-center text-sm text-gray-500">
          {data.total > items.length
            ? `顯示 ${items.length} / ${data.total} 筆`
            : `共 ${data.total} 筆`}
        </p>
      )}

      {delayModalTarget && (
        <PaymentDelayModal
          acceptance={delayModalTarget}
          onClose={() => setDelayModalTarget(null)}
        />
      )}
    </div>
  );
}

function PaymentCard({
  acceptance,
  onRequestDelay,
}: {
  acceptance: Acceptance;
  onRequestDelay: () => void;
}) {
  const isPaid = !!acceptance.pay_date;
  const delayState = acceptance.payment_delay_state || 'none';
  const canRequestDelay =
    !isPaid &&
    acceptance.state !== 'cancelled' &&
    (delayState === 'none' || delayState === 'rejected');

  return (
    <div className="p-4">
      <Link
        to={`/acceptance/${acceptance.id}`}
        className="flex items-center justify-between hover:bg-gray-50"
      >
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900">
            {acceptance.project_name || acceptance.sale_order_name || acceptance.name}
          </p>
          <p className="truncate text-sm text-gray-500">
            {acceptance.partner_name || '---'}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
            <span>{acceptance.name}</span>
            {acceptance.completion_date && (
              <span>完工: {formatDate(acceptance.completion_date)}</span>
            )}
            {acceptance.employee_name && (
              <span>{acceptance.employee_name}</span>
            )}
          </div>
        </div>
        <div className="ml-4 flex flex-col items-end gap-1.5">
          <span className="text-sm font-medium text-gray-700">
            {acceptance.balance_amount != null
              ? `NT$${acceptance.balance_amount.toLocaleString()}`
              : '---'}
          </span>
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              isPaid
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {isPaid ? `已付款 ${formatDate(acceptance.pay_date)}` : '未付款'}
          </span>
          {delayState !== 'none' && (
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${DELAY_STATE_STYLES[delayState]}`}
            >
              {DELAY_STATE_LABELS[delayState]}
            </span>
          )}
          <ChevronRight className="h-4 w-4 text-gray-300" />
        </div>
      </Link>
      {canRequestDelay && (
        <button
          onClick={(e) => {
            e.preventDefault();
            onRequestDelay();
          }}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 py-2 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100"
        >
          <Clock className="h-4 w-4" />
          申請延後付款
        </button>
      )}
    </div>
  );
}

const DELAY_DAY_OPTIONS = [
  { value: '7', label: '7 天' },
  { value: '15', label: '15 天' },
  { value: '30', label: '30 天' },
  { value: '60', label: '60 天' },
  { value: 'other', label: '其他（請寫在說明中）' },
] as const;

function PaymentDelayModal({
  acceptance,
  onClose,
}: {
  acceptance: Acceptance;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [delayDays, setDelayDays] = useState('');
  const mutation = useRequestPaymentDelay();

  const isOther = delayDays === 'other';
  const canSubmit = reason.trim() && delayDays && !mutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    mutation.mutate(
      {
        id: acceptance.id,
        reason: reason.trim(),
        delayDays: isOther ? 0 : Number(delayDays),
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-lg rounded-t-2xl bg-white p-6 sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">申請延後付款</h2>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <p className="mb-4 text-sm text-gray-500">
          {acceptance.project_name || acceptance.name} —{' '}
          {acceptance.partner_name || ''}
        </p>

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            延後天數 <span className="text-red-500">*</span>
          </label>
          <select
            value={delayDays}
            onChange={(e) => setDelayDays(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm focus:border-primary focus:outline-none"
          >
            <option value="" disabled>
              請選擇延後天數
            </option>
            {DELAY_DAY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            延後原因{isOther ? '（含希望延後日期）' : ''}{' '}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={
              isOther
                ? '請說明延後原因及希望延後至的日期...'
                : '請說明延後付款的原因...'
            }
            className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {mutation.isPending ? '送出中...' : '送出申請'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
